import {
  FC,
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
} from 'react';

import { useSubscriptionFeatures } from '~hooks/useSubscriptionFeatures';
import { useRequest } from '~hooks/useRequest';
import { getItemPresets } from '~services/item';
import { ItemPreset, ItemPresetParse } from '~services/item/types';
import { Nullable } from '~globals/types/commons';
import { PlanFeaturesTypes } from '~globals/types/enums';
import { convertPresetFilterToItemSearchFilters } from '~utils/order';
import { camelCaseSerializer } from '~utils/serializer';

import { useDashboardVariantV2CarrierContext } from './Context';
import { useDashboardVariantV2CarrierSelectDataContext } from './SelectDataContext';
import { useDashboardVariantV2CarrierCollectFiltersContext } from './CollectFiltersContext';
import { useDashboardVariantV2CarrierInWarehouseFiltersContext } from './InWarehouseFiltersContext';
import { useDashboardVariantV2CarrierInTransitFiltersContext } from './InTransitFiltersContext';
import { useDashboardVariantV2CarrierFinalizedFiltersContext } from './FinalizedFiltersContext';
import { DashboardVariantV2CarrierPresetsContextValue } from './types';

const DashboardVariantV2CarrierPresetsContext =
  createContext<DashboardVariantV2CarrierPresetsContextValue>({
    data: [],
    loading: false,
    reload: () => {},
    selectedPreset: {
      value: null,
      handleChange: () => {},
      reset: () => {},
    },
    handleApplyFiltersByPreset: () => {},
  });

export const useDashboardVariantV2CarrierPresetsContext =
  (): DashboardVariantV2CarrierPresetsContextValue =>
    useContext(DashboardVariantV2CarrierPresetsContext);

const DashboardVariantV2CarrierPresetsProvider: FC = ({ children }) => {
  const { hasAccessToFeature } = useSubscriptionFeatures();
  const {
    ignoreRefreshBySocket: { toggleValue: toggleIgnoreRefreshBySocket },
    expandedHasChangeOrdersAlert: {
      toggleValue: toggleExpandedHasChangeOrdersAlert,
    },
    modifiedItemsIdBySocket: { setValue: setModifiedItemsIdBySocket },
    activeTab: { getIsActiveTab },
  } = useDashboardVariantV2CarrierContext();

  const {
    vehicles: { list: vehicles },
    drivers: { list: drivers },
    services: { list: services },
    logisticsZones: { list: logisticsZones },
    assignedUser: { list: assignedUsers },
    giverCompany: { list: giverCompanies },
  } = useDashboardVariantV2CarrierSelectDataContext();

  const filtersCollect = useDashboardVariantV2CarrierCollectFiltersContext();
  const filtersInWarehouse =
    useDashboardVariantV2CarrierInWarehouseFiltersContext();
  const filtersInTransit =
    useDashboardVariantV2CarrierInTransitFiltersContext();
  const filtersFinalized =
    useDashboardVariantV2CarrierFinalizedFiltersContext();

  const { autocompletesRefs, filters, forceRequest } = useMemo(() => {
    if (getIsActiveTab('finalized')) return filtersFinalized;

    if (getIsActiveTab('inTransit')) return filtersInTransit;

    if (getIsActiveTab('collect')) return filtersCollect;

    return filtersInWarehouse;
  }, [
    filtersCollect,
    filtersFinalized,
    filtersInTransit,
    filtersInWarehouse,
    getIsActiveTab,
  ]);

  const [presets, setPresets] = useState<ItemPresetParse[]>([]);
  const isValidToFetchPresets = useMemo(
    () => hasAccessToFeature(PlanFeaturesTypes.LimitPaid),
    [hasAccessToFeature],
  );
  const [, loadingPresets, , executeReloadPresets] = useRequest({
    request: getItemPresets,
    isValidToRequest: isValidToFetchPresets,
    initialLoading: isValidToFetchPresets,
    payload: null,
    withPostSuccess: (response) => {
      const presetsResponse = response.data?.data.results as ItemPreset[];

      const parsePresets: ItemPresetParse[] = presetsResponse.map((preset) => ({
        ...preset,
        data: camelCaseSerializer.serialize(JSON.parse(preset.data)),
      }));

      parsePresets.sort((a, b) => a.description.localeCompare(b.description));

      setPresets(parsePresets);
    },
  });

  const reloadPresets = useCallback(() => {
    executeReloadPresets(null);
  }, [executeReloadPresets]);

  const [selectedPreset, setSelectedPreset] =
    useState<Nullable<ItemPresetParse>>(null);

  const handleApplyFiltersByPreset = useCallback(
    (preset: ItemPresetParse) => {
      const currentFilters = convertPresetFilterToItemSearchFilters(
        preset.data,
        {
          vehicles,
          drivers,
          services,
          logisticsZones,
          assignedUsers,
          giverCompanies,
        },
        {
          limitNoPro: hasAccessToFeature(PlanFeaturesTypes.LimitNotPro),
          mgmZones: hasAccessToFeature(PlanFeaturesTypes.MgmZones),
        },
      );

      filters.handleChangeFilters?.(currentFilters);

      filters.setFiltersActives(currentFilters);

      autocompletesRefs.vehicles.current?.setValue(
        currentFilters.vehicles ?? [],
      );
      autocompletesRefs.drivers.current?.setValue(currentFilters.drivers ?? []);
      autocompletesRefs.services.current?.setValue(
        currentFilters.services ?? [],
      );
      autocompletesRefs.logisticsZones.current?.setValue(
        currentFilters.zones ?? [],
      );
      autocompletesRefs.assignedUser.current?.setValue(
        currentFilters.assignedUser ?? null,
      );
      autocompletesRefs.sourceImports.current?.setValue(
        currentFilters.sourceImports ?? [],
      );
      autocompletesRefs.giverCompany.current?.setValue(
        currentFilters.giverCompany ?? null,
      );
      autocompletesRefs.driver.current?.setValue(currentFilters.driver ?? null);
      autocompletesRefs.itemState.current?.setValue(
        currentFilters.itemState ?? [],
      );
      autocompletesRefs.collectState.current?.setValue(
        currentFilters.collectState ?? [],
      );

      filters.setIsFilterApplied(true);

      toggleIgnoreRefreshBySocket(false);
      toggleExpandedHasChangeOrdersAlert(false);
      setModifiedItemsIdBySocket([]);

      forceRequest.toggleValue(true);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      vehicles,
      drivers,
      services,
      logisticsZones,
      assignedUsers,
      giverCompanies,
      hasAccessToFeature,
      filters.handleChangeFilters,
      filters.setFiltersActives,
      filters.setIsFilterApplied,
      toggleIgnoreRefreshBySocket,
      toggleExpandedHasChangeOrdersAlert,
      setModifiedItemsIdBySocket,
      forceRequest.toggleValue,
    ],
  );

  const handleChangePreset = useCallback(
    (newPreset: Nullable<ItemPresetParse>) => {
      setSelectedPreset(newPreset);

      if (newPreset) {
        handleApplyFiltersByPreset(newPreset);
      }
    },
    [handleApplyFiltersByPreset],
  );

  const handleResetSelectedPreset = useCallback(() => {
    setSelectedPreset(null);
  }, []);

  return (
    <DashboardVariantV2CarrierPresetsContext.Provider
      value={{
        data: presets,
        loading: loadingPresets,
        reload: reloadPresets,
        selectedPreset: {
          value: selectedPreset,
          handleChange: handleChangePreset,
          reset: handleResetSelectedPreset,
        },
        handleApplyFiltersByPreset,
      }}
    >
      {children}
    </DashboardVariantV2CarrierPresetsContext.Provider>
  );
};

export default DashboardVariantV2CarrierPresetsProvider;
